import { BlockStack, Card, ChoiceList, InlineStack, RadioButton, Tabs, TextField } from '@shopify/polaris';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BoldText from '@/components/BoldText';
import LayoutSettingsCustom from '@/components/LayoutSetting/LayoutSettingsCustom';
import { FILTER_NUMERIC_REGEX, MAX_LENGTH_NUMBER } from '@/constants';
import { DateChoicesEnum, OrderLimitType, PricingPlan, WorkingDayType } from '@/constants/enum';
import {
  deliveryDisableSelector,
  deliverySettingSelector,
  deliverySpecificWorkingDaySelector,
  deliveryWeeklyWorkingDaySelector,
  deliveryWeeklyWorkingDaysLastStateSelector,
  handleSaveDeliverySettings,
  handleSaveDeliveryWeeklyWorkingDays,
} from '@/redux/slice/delivery.slice';
import { getWeekdaysFromSpecificWorkingDays } from '@/utils';
import { useDispatch, useSelector } from 'react-redux';
import DeliveryTime from './DeliveryTime';
import { PlanMessage } from '@/components/PlanMessage';

interface DeliveryAvailabilityProps {
  time24h: { id: number; value: string }[];
}

const DeliveryAvailability: FC<DeliveryAvailabilityProps> = ({ time24h }) => {
  const { t } = useTranslation('deliverySettings');
  const dispatch = useDispatch();
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);
  const deliveryWeeklyWorkingDaysLastState = useSelector(deliveryWeeklyWorkingDaysLastStateSelector);
  const deliverySpecificDays = useSelector(deliverySpecificWorkingDaySelector);
  const deliveryDisable = useSelector(deliveryDisableSelector);
  const [selectTabDays, setSelectTabDays] = useState<number>(0);

  const handleSelectDeliveryAvailability = (value: WorkingDayType) => {
    dispatch(handleSaveDeliverySettings({ workingDayType: value }));
    if (value === WorkingDayType.EVERYDAY) {
      dispatch(
        handleSaveDeliveryWeeklyWorkingDays(
          deliveryWeeklyWorkingDays.map((item) => ({
            ...item,
            isEnable: true,
          })),
        ),
      );
    }
    if (value === WorkingDayType.WEEKDAYS && deliveryWeeklyWorkingDaysLastState) {
      dispatch(
        handleSaveDeliveryWeeklyWorkingDays(
          deliveryWeeklyWorkingDays.map((item, index) => ({
            ...item,
            isEnable: deliveryWeeklyWorkingDaysLastState[index].isEnable,
          })),
        ),
      );
    }
    if (value === WorkingDayType.CUSTOM) {
      const weekdaysFromSpecificDates = getWeekdaysFromSpecificWorkingDays(deliverySpecificDays);
      dispatch(
        handleSaveDeliveryWeeklyWorkingDays(
          deliveryWeeklyWorkingDays.map((item) => ({
            ...item,
            isEnable: weekdaysFromSpecificDates.length > 0 ? weekdaysFromSpecificDates.includes(item.day) : false,
          })),
        ),
      );
    }
  };

  const handleSelectOrderLimit = (value: OrderLimitType) => {
    dispatch(handleSaveDeliverySettings({ orderLimitType: value }));
  };

  const deliveryAvailabilityOptions = useMemo(() => {
    return [
      { label: t('sections.deliveryAvailability.workingDay.everyDay'), value: WorkingDayType.EVERYDAY },
      { label: t('sections.deliveryAvailability.workingDay.dayOfWeek'), value: WorkingDayType.WEEKDAYS },
      { label: t('sections.deliveryAvailability.workingDay.datesOfYear'), value: WorkingDayType.CUSTOM },
    ];
  }, [t]);

  const deliveryShortDayLabels = useMemo(() => {
    return [
      t('sections.weekDaysShort.0'),
      t('sections.weekDaysShort.1'),
      t('sections.weekDaysShort.2'),
      t('sections.weekDaysShort.3'),
      t('sections.weekDaysShort.4'),
      t('sections.weekDaysShort.5'),
      t('sections.weekDaysShort.6'),
    ];
  }, [t]);

  const deliveryLongDayLabels = useMemo(() => {
    return [
      t('sections.weekDaysLong.0'),
      t('sections.weekDaysLong.1'),
      t('sections.weekDaysLong.2'),
      t('sections.weekDaysLong.3'),
      t('sections.weekDaysLong.4'),
      t('sections.weekDaysLong.5'),
      t('sections.weekDaysLong.6'),
    ];
  }, [t]);

  const workingDayChecks = useMemo(() => {
    return deliveryWeeklyWorkingDays
      .filter((item) => item.day >= 0)
      .sort((a, b) => {
        const dayA = a.day === 0 ? 7 : a.day;
        const dayB = b.day === 0 ? 7 : b.day;
        return dayA - dayB;
      });
  }, [deliveryWeeklyWorkingDays]);

  const workingDayTabs = useMemo(() => {
    let weeklyWorkingDays = workingDayChecks.map((item) => ({
      id: item.day.toString(),
      content: deliveryShortDayLabels[item.day],
      panelID: item.day.toString(),
    }));

    return weeklyWorkingDays;
  }, [deliveryShortDayLabels, workingDayChecks]);

  const selectedWorkingDay = useMemo(() => {
    if (workingDayTabs.length > 0 && deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_DAY) {
      return deliveryWeeklyWorkingDays.find((item) => item.day === Number(workingDayTabs[selectTabDays]?.id));
    }
  }, [deliverySettings.orderLimitType, deliveryWeeklyWorkingDays, selectTabDays, workingDayTabs]);

  const handleChangeOrderLimit = (value: string) => {
    const onlyNumbers = value.replace(FILTER_NUMERIC_REGEX, '').slice(0, MAX_LENGTH_NUMBER);
    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((item) => {
          if (item.day === selectedWorkingDay?.day) {
            return {
              ...item,
              orderLimitPerDay: Number(onlyNumbers),
            };
          }
          return item;
        }),
      ),
    );
  };

  useEffect(() => {
    if (
      deliverySettings.deliveryChoice === DateChoicesEnum.SINGLEDATE &&
      deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT
    ) {
      handleSelectOrderLimit(OrderLimitType.LIMIT_PER_DAY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliverySettings.deliveryChoice, deliverySettings.orderLimitType]);

  return (
    <LayoutSettingsCustom title={t('sections.deliveryAvailability.title')} helpText={t('sections.deliveryAvailability.helpText')}>
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="200">
            <BoldText>{t('orderLimit.title')}</BoldText>
            <RadioButton
              id="limitPerDay"
              name="orderLimitPickup"
              onChange={() => handleSelectOrderLimit(OrderLimitType.LIMIT_PER_DAY)}
              label={t('orderLimit.listOderLimit.limitPerDay')}
              checked={deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_DAY}
            />
            {deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_DAY && (
              <>
                <Tabs tabs={workingDayTabs} selected={selectTabDays} onSelect={(value) => setSelectTabDays(value)} fitted />
                <TextField
                  label={t('orderLimit.limitPerDayLabel', {
                    day: deliveryLongDayLabels[workingDayChecks[selectTabDays]?.day],
                  })}
                  placeholder={t('orderLimit.placeholder')}
                  helpText={t('orderLimit.helpText')}
                  type="number"
                  value={selectedWorkingDay?.orderLimitPerDay?.toString()}
                  onChange={(value) => handleChangeOrderLimit(value)}
                  autoComplete="off"
                  min={0}
                  step={1}
                />
              </>
            )}
            {deliverySettings.deliveryChoice !== DateChoicesEnum.SINGLEDATE && (
              <InlineStack blockAlign="center" align="space-between">
                <RadioButton
                  id="limitPerTimeSlot"
                  name="orderLimitPickup"
                  onChange={() => handleSelectOrderLimit(OrderLimitType.LIMIT_PER_TIMESLOT)}
                  label={t('orderLimit.listOderLimit.limitPerTimeSlot')}
                  checked={deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT}
                  disabled={deliveryDisable.isDisableOrderLimitPerTimeSlot}
                />
                {deliveryDisable.isDisableOrderLimitPerTimeSlot && <PlanMessage plan={PricingPlan.Essential} />}
              </InlineStack>
            )}
          </BlockStack>
        </Card>
        <Card>
          <div id="delivery-specific-workingdays">
            <BlockStack gap="200">
              <BoldText>{t('sections.deliveryAvailability.workingDay.title')}</BoldText>
              <ChoiceList
                name="deliveryAvailability"
                title={t('sections.deliveryAvailability.title')}
                titleHidden
                choices={deliveryAvailabilityOptions}
                selected={[deliverySettings.workingDayType]}
                onChange={(value) => handleSelectDeliveryAvailability(value[0] as WorkingDayType)}
              />
              <div id="delivery-time-slot">
                <DeliveryTime time24h={time24h} />
              </div>
            </BlockStack>
          </div>
        </Card>
      </BlockStack>
    </LayoutSettingsCustom>
  );
};

export default DeliveryAvailability;
